import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export type { FeedbackBundle, Locale, LocaleStrings } from "./utils";
export { getAssessmentData, getFeedbackBundle, getLocale } from "./utils";

import type { Locale } from "./utils";
import { getLocale } from "./utils";

function detectLocale(): Locale {
  const path = window.location.pathname;
  if (path.startsWith("/nl")) return "nl";
  if (path.startsWith("/fr")) return "fr";
  if (path.startsWith("/en")) return "en";
  const lang = (navigator.language || "en").slice(0, 2).toLowerCase();
  if (lang === "nl") return "nl";
  if (lang === "fr") return "fr";
  return "en";
}

const LocaleContext = createContext<Locale>("en");

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
    const path = window.location.pathname;
    if (path === "/" || path === "") {
      window.history.replaceState(
        null,
        "",
        `/${locale}/${window.location.search}`,
      );
    }
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      const ogValue =
        locale === "nl" ? "nl_BE" : locale === "fr" ? "fr_BE" : "en_GB";
      ogLocale.setAttribute("content", ogValue);
    }
  }, [locale]);

  return (
    <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useT(): (
  key: string,
  vars?: Record<string, string | number>,
) => string {
  const locale = useLocale();
  const ui = getLocale(locale).ui;
  return (key: string, vars?: Record<string, string | number>) => {
    const raw = ui[key] ?? key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
  };
}
